import { useEffect, useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "State/hooks";
import { useNavigate } from "react-router-dom";
import {
  getFallsDashboardData,
  getHubFalls,
} from "@intelligentlilli/api-layer";
import { SERVER } from "../config";
// State
import { updateServiceUserData } from "../../State/slices/serviceUsersData-slice";
// Utils
import { areArraysEqualById } from "../Utils";

export const useFetchFalls = (isFalls: boolean) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [error, setError] = useState<null | boolean>(null); // boolean
  const [loading, setLoading] = useState(false);
  const [lastFetched, setLastFetched] = useState<null | Date>(null); // string | null

  const initialFetchDone = useRef(false); // Track initial fetch completion

  const serviceUsers = useAppSelector(
    (state) => state.serviceUsers,
    (prev, next) => areArraysEqualById(prev, next) // custom equality check
  );

  // Memoize serviceUserIds
  const serviceUserIds = serviceUsers.map((su) => su.id); // TO DO FALLS: remove when pushing to PROD
  const stringifiedIds = JSON.stringify(serviceUserIds);

  useEffect(() => {
    const arrayOfIDs: number[] = JSON.parse(stringifiedIds);

    if (!isFalls && (!arrayOfIDs || arrayOfIDs.length === 0)) return;

    const fetchFalls = async (suIDs: string[]) => {
      if (!initialFetchDone.current) setLoading(true);

      suIDs?.map(async (hubId) => {
        try {
          const hubFallsData = await getHubFalls({
            server: SERVER,
            hubId,
            origin: "web",
          });

          if (hubFallsData.ok && hubFallsData?.body) {
            dispatch(
              updateServiceUserData({
                hubId: hubId,
                update: {
                  falls: hubFallsData.body,
                },
              })
            );
            setLastFetched(new Date());
            setError(false);
          } else {
            console.error("Res not okay for getHubFalls, hubId:", hubId);
            setError(true);
          }
        } catch (error: any) {
          if (error.status === 401) {
            return navigate("/login");
          } else {
            console.error("Error with getHubFalls:", error);
            // Don't show the error message if the issue is the lack of device "No devices found for service user"
            if (error.status !== 404) {
              setError(true);
            }
          }
        } finally {
          // Mark initial fetch as done after completion
          if (!initialFetchDone.current) {
            setLoading(false);
            initialFetchDone.current = true;
          }
        }
      });
    };

    const fetchHeartRateAndBreathingRate = async (suIDs: number[]) => {
      try {
        const hubHRBreathingData = await getFallsDashboardData({
          server: SERVER,
          suIDs: suIDs,
          origin: "web",
        });

        if (hubHRBreathingData.ok && hubHRBreathingData.body?.serviceUsers) {
          //  match the service users in the api response to the SU ids held in redux
          hubHRBreathingData.body.serviceUsers.forEach((serviceUser) => {
            if (arrayOfIDs.includes(serviceUser.serviceUserId)) {
              const sleepStats = serviceUser.devices
                .map((device) => device.recentSleepStatistics)
                .filter(Boolean); // Remove undefined values

              dispatch(
                updateServiceUserData({
                  hubId: serviceUser.serviceUserId,
                  update: {
                    hrBreathingData: sleepStats[0] || {},
                  },
                })
              );
            }
          });
        }
      } catch (error: any) {
        if (error.status === 401) {
          return navigate("/login");
        } else {
          console.error("Error with getFallsBreathingHeartRate:", error);

          // Reset HR and breathing data in Redux if there's an error
          arrayOfIDs.forEach((hubId) => {
            dispatch(
              updateServiceUserData({
                hubId,
                update: {
                  hrBreathingData: {},
                },
              })
            );
          });
        }
      }
    };

    fetchFalls(arrayOfIDs);

    if (arrayOfIDs && arrayOfIDs?.length > 0) {
      fetchHeartRateAndBreathingRate(arrayOfIDs);
    }

    const interval = window.setInterval(() => {
      fetchFalls(arrayOfIDs);

      if (arrayOfIDs && arrayOfIDs?.length > 0) {
        fetchHeartRateAndBreathingRate(arrayOfIDs);
      }
      console.log("fetched falls");
    }, 15000); // every 15 seconds

    // Remove polling when the component unmounts
    return () => interval && window.clearInterval(interval);
  }, [stringifiedIds, isFalls, navigate, dispatch]);

  return { error, lastFetched, loading };
};
